<template>
  <RevDrawer
    :close-button-label="i18n(translations.cancelButton)"
    :name="MODAL_NAME"
    :title="i18n(translations.modalTitle)"
  >
    <template #body>
      <RevLoadingScreen
        v-if="isSubmitting"
        :text="i18n(translations.updatingBankingDetails)"
      />
      <RevForm
        v-else
        :id="FORM_ID"
        :initial-values="INITIAL_FORM_VALUES"
        :validate="validate()"
        @submit="handleSubmit"
      >
        <template #default="{ values, errors }">
          <div class="mb-16">
            <RevInputText
              id="accountNumber"
              v-model="values.accountNumber"
              class="mb-12"
              :error="errors.accountNumber"
              :label="i18n(translations.accountNumberInputLabel)"
              type="text"
            />

            <RevInputText
              id="routingNumber"
              v-model="values.routingNumber"
              class="mb-12"
              :error="errors.routingNumber"
              :label="i18n(translations.routingNumberInputLabel)"
              type="text"
            />
          </div>
        </template>
      </RevForm>
    </template>

    <template #footer="{ close }">
      <RevButton
        id="edit_banking_details_button"
        :disabled="isUpdating"
        :form="FORM_ID"
        full-width="always"
        type="submit"
        variant="primary"
      >
        {{ i18n(translations.submitButton) }}
      </RevButton>

      <RevLink id="cancel_form" class="m-auto mt-16 w-full" @click="close">
        {{ i18n(translations.cancelButton) }}
      </RevLink>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { ref } from 'vue'

import { updateProfile } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'

import { FORM_ID, MODAL_NAME } from './BankingDetailsFormModal.constants'
import translations from './BankingDetailsFormModal.translations'

const { openErrorToast, openSuccessToast } = useTheToast()
const i18n = useI18n()
const emit = defineEmits(['update-banking-details'])

const isSubmitting = ref(false)

const props = defineProps<{
  bankingDetails: Record<string, string> | null | undefined
}>()

type FormValues = {
  routingNumber: string
  accountNumber: string
}

const INITIAL_FORM_VALUES: FormValues = {
  routingNumber: props.bankingDetails?.routingNumber || '',
  accountNumber: props.bankingDetails?.accountNumber || '',
}

const isUpdating = ref(false)

function validate() {
  const requiredErrorMessage = i18n(translations.required)

  return makeValidate<FormValues>({
    routingNumber: [required(requiredErrorMessage)],
    accountNumber: [required(requiredErrorMessage)],
  })
}

async function handleSubmit(values: FormValues) {
  isSubmitting.value = true

  try {
    await $httpFetch(updateProfile, {
      body: {
        bankingInformation: {
          accountNumber: values.accountNumber,
          routingNumber: values.routingNumber,
        },
      },
    })

    openSuccessToast({
      title: i18n(translations.bankingDetailsUpdatedToastSuccessTitle),
      content: i18n(translations.bankingDetailsUpdatedToastSuccessContent),
    })

    emit('update-banking-details')

    closeModal(MODAL_NAME)
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
